/**
 * useChartLayout — gerencia a lista de painéis ativos no EquipmentDetail.
 *
 * Persiste em localStorage por equipment_id pra que o usuário recupere
 * a layout configurada ao voltar pra mesmo ativo depois.
 */

import { useCallback, useEffect, useState } from "react";
import type { MetricKey } from "./ChartPanel";

export interface PanelConfig {
  id: string;
  metric: MetricKey;
  showDoor: boolean;
  /** Quando metric="custom", IDs das séries selecionadas. */
  customSeriesIds?: string[];
}

const DEFAULT_PANELS: PanelConfig[] = [
  { id: "default-temp", metric: "temperature", showDoor: true  },
  { id: "default-elec", metric: "energy",      showDoor: false },
];

function storageKey(equipmentId: string): string {
  return `envpulse:chart-layout:${equipmentId}`;
}

function loadLayout(equipmentId: string): PanelConfig[] {
  try {
    const raw = localStorage.getItem(storageKey(equipmentId));
    if (!raw) return DEFAULT_PANELS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_PANELS;
    return parsed.filter(
      (p): p is PanelConfig =>
        p && typeof p.id === "string" && typeof p.metric === "string" && typeof p.showDoor === "boolean",
    ).map((p) => ({
      // Hidrata `customSeriesIds` se vier do storage (forward-compat)
      ...p,
      customSeriesIds: Array.isArray((p as PanelConfig).customSeriesIds)
        ? (p as PanelConfig).customSeriesIds
        : undefined,
    }));
  } catch {
    return DEFAULT_PANELS;
  }
}

function saveLayout(equipmentId: string, panels: PanelConfig[]): void {
  try {
    localStorage.setItem(storageKey(equipmentId), JSON.stringify(panels));
  } catch {
    // Ignora quota erro / privacy mode
  }
}

export function useChartLayout(equipmentId: string) {
  const [panels, setPanels] = useState<PanelConfig[]>(() => loadLayout(equipmentId));

  // Re-carrega se o equipmentId mudar (navegação entre ativos)
  useEffect(() => {
    setPanels(loadLayout(equipmentId));
  }, [equipmentId]);

  // Persiste em qualquer mudança
  useEffect(() => {
    saveLayout(equipmentId, panels);
  }, [equipmentId, panels]);

  const addPanel = useCallback(() => {
    setPanels((prev) => [
      ...prev,
      {
        id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        metric: "temperature",
        showDoor: true,
      },
    ]);
  }, []);

  const removePanel = useCallback((id: string) => {
    setPanels((prev) => (prev.length > 1 ? prev.filter((p) => p.id !== id) : prev));
  }, []);

  const updatePanel = useCallback((id: string, patch: Partial<PanelConfig>) => {
    setPanels((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  const resetLayout = useCallback(() => {
    setPanels(DEFAULT_PANELS);
  }, []);

  return { panels, addPanel, removePanel, updatePanel, resetLayout };
}
